/**
 * The categories this store files under.
 *
 * Thirteen ship with the platform and the executive Logistics board names
 * them, so they cannot be removed — a store that deleted "Tools" would take a
 * column off a board it does not own. Beyond those, a storekeeper adds what
 * the store actually holds: an airport keeping a separate shelf for de-icing
 * chemicals should be able to say so rather than file them under "General
 * consumables" and lose them.
 */
import { useState } from "react";
import { categoriesOf, type StoresRegister } from "../../domain/stores";
import { addCategory, removeCategory, type Result } from "../../domain/storesEdit";
import { Button, Card, Field, Input, Table } from "../../design/components";
import { t, tn } from "../../i18n";
import s from "./Stores.module.css";

export function CategoriesCard({
  register,
  who,
  onApply,
}: {
  register: StoresRegister;
  who: string;
  onApply: (r: Result) => void | Promise<void>;
}) {
  const [label, setLabel] = useState("");
  const [what, setWhat] = useState("");
  const all = categoriesOf(register);
  const held = (key: string) => register.items.filter((i) => i.category === key && !i.obsoleted).length;

  return (
    <Card
      collapsible
      title="Categories"
      sub="Built-in categories and the ones you add"
    >
      <Table>
        <thead>
          <tr>
            <th>{t("Category")}</th>
            <th>{t("What goes in it")}</th>
            <th className={s.num}>{t("Lines")}</th>
            <th />
          </tr>
        </thead>
        <tbody data-testid="stores-categories">
          {all.map((c) => (
            <tr key={c.key}>
              <td>
                {t(c.label)}
                <div className={s.sku}>{c.key}</div>
              </td>
              <td className={s.why}>{t(c.what)}</td>
              <td className={s.num}>{held(c.key)}</td>
              <td>
                {c.added ? (
                  <Button
                    size="sm"
                    data-testid={`category-remove-${c.key}`}
                    onClick={() => void onApply(removeCategory(register, c.key, { name: who }))}
                  >
                    {t("Remove")}
                  </Button>
                ) : (
                  <span className={s.unknown}>{t("ships with the platform")}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className={s.filters} style={{ marginTop: 12 }}>
        <Field label={t("A category this store needs")}>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t("De-icing chemicals")}
            aria-label={t("A category this store needs")}
            data-testid="category-label"
          />
        </Field>
        <Field label={t("What goes in it")}>
          <Input
            value={what}
            onChange={(e) => setWhat(e.target.value)}
            placeholder={t("fluids, drums, spray nozzles")}
            aria-label={t("What goes in it")}
            data-testid="category-what"
          />
        </Field>
        <Button
          data-testid="category-add"
          onClick={() => {
            void onApply(addCategory(register, { label, what }, { name: who }));
            setLabel("");
            setWhat("");
          }}
        >
          {t("Add the category")}
        </Button>
      </div>
      <p className={s.meta}>
        {tn(
          all.filter((c) => c.added).length,
          "{n} category has been added to the ones the platform ships with. A category with lines filed under it cannot be removed until they are moved.",
          "{n} categories have been added to the ones the platform ships with. A category with lines filed under it cannot be removed until they are moved.",
        )}
      </p>
    </Card>
  );
}
